import { useEffect, useId, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { authClient } from '../../utils/amplifyClient';
import { createLocation } from '../../graphql/mutations';
import { useRegionAndLocationContext } from '../../contexts/RegionAndLocationContext';
import { buildInterimLocationDescription } from '../../utils/regionLocationHierarchy';
import type { Location } from '../../types/schema';
import { Btn, BtnIconOnly, IconClose, InputField, Select, type SelectOption } from '../ui';
import '../../css/CompanyResourcesPage.css';

interface AddLocationModalProps {
    companyId: string;
    isOpen: boolean;
    onClose: () => void;
    onCreated: (location: Location) => void | Promise<void>;
}

export const AddLocationModal = ({
    companyId,
    isOpen,
    onClose,
    onCreated,
}: AddLocationModalProps) => {
    const { companyGroupedData, addLocation, refreshRegionAndLocation } = useRegionAndLocationContext();
    const fieldId = useId();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [regionalDivisionId, setRegionalDivisionId] = useState('');
    const [districtId, setDistrictId] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const isMountedRef = useRef(true);
    const dismissOnClickRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const regionOptions: SelectOption[] = useMemo(
        () =>
            [...companyGroupedData]
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map((region) => ({ value: region.id, label: region.name || 'Region' })),
        [companyGroupedData],
    );

    const districtOptions: SelectOption[] = useMemo(
        () =>
            companyGroupedData
                .filter((region) => !regionalDivisionId || region.id === regionalDivisionId)
                .flatMap((region) =>
                    region.districts.map((district) => ({
                        value: district.id,
                        label: `${district.name} (${region.name})`,
                    })),
                ),
        [companyGroupedData, regionalDivisionId],
    );

    if (!isOpen) return null;

    const hasRegions = regionOptions.length > 0;

    const validate = () => {
        const next: Record<string, string> = {};
        if (!name.trim()) next.name = 'Location name is required';
        if (!regionalDivisionId) next.region = 'Region is required';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate() || !companyId) return;
        setSaving(true);
        try {
            const validDistrictId = districtOptions.some((district) => district.value === districtId)
                ? districtId
                : '';
            const result = await authClient.graphql({
                query: createLocation,
                variables: {
                    input: {
                        name: name.trim(),
                        companyId,
                        regionalDivisionId,
                        description: buildInterimLocationDescription(
                            validDistrictId || null,
                            description.trim() || null,
                        ),
                    },
                },
            });
            const created = result.data.createLocation as Location;
            addLocation(created);
            await refreshRegionAndLocation();
            if (!isMountedRef.current) return;
            await onCreated(created);
        } catch (error) {
            console.error('Error creating location:', error);
            Sentry.captureException(error);
            if (isMountedRef.current) {
                setErrors({ _form: 'Failed to save. Please try again.' });
            }
        } finally {
            if (isMountedRef.current) setSaving(false);
        }
    };

    return (
        <div
            className="cr-modal-overlay"
            onMouseDown={(event) => {
                dismissOnClickRef.current = event.target === event.currentTarget;
            }}
            onClick={(event) => {
                if (dismissOnClickRef.current && event.target === event.currentTarget) {
                    onClose();
                }
                dismissOnClickRef.current = false;
            }}
        >
            <div
                className="cr-modal cr-modal--service-center"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="add-location-modal-title"
            >
                <div className="cr-modal-header">
                    <h2 id="add-location-modal-title" className="cr-modal-title">
                        Add Location
                    </h2>
                    <BtnIconOnly size="sm" icon={<IconClose />} ariaLabel="Close" onClick={onClose} />
                </div>
                <div className="cr-modal-body">
                    {errors._form && (
                        <div className="cr-error-banner" role="alert">
                            ⚠️ {errors._form}
                        </div>
                    )}

                    {!hasRegions && (
                        <div className="cr-error-banner" role="alert">
                            ⚠️ No regions exist yet. A location must belong to a region, so create a
                            region first (Company Resources → Regions) before adding a location.
                        </div>
                    )}

                    <div className="cr-field">
                        <InputField
                            label="Location Name *"
                            inputId={`${fieldId}-name`}
                            value={name}
                            onChange={setName}
                            hasError={Boolean(errors.name)}
                            ariaDescribedBy={errors.name ? `${fieldId}-name-error` : undefined}
                            autoFocus
                        />
                        {errors.name && (
                            <div id={`${fieldId}-name-error`} className="cr-field-error-text">
                                {errors.name}
                            </div>
                        )}
                    </div>

                    <div className="cr-field">
                        <label className="cr-field-label" htmlFor={`${fieldId}-description`}>
                            Description
                        </label>
                        <textarea
                            id={`${fieldId}-description`}
                            className="cr-field-textarea"
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            rows={3}
                            placeholder="Optional"
                        />
                    </div>

                    <div className="cr-field">
                        <span className="cr-field-label" id={`${fieldId}-region-label`}>
                            Region *
                        </span>
                        <Select
                            inputId={`${fieldId}-region`}
                            ariaLabel="Region"
                            ariaDescribedBy={
                                errors.region ? `${fieldId}-region-error` : `${fieldId}-region-hint`
                            }
                            placeholder="Select region"
                            emptyMessage="No regions yet"
                            options={regionOptions}
                            value={regionalDivisionId}
                            disabled={saving}
                            onChange={(nextRegionId) => {
                                setRegionalDivisionId(nextRegionId);
                                setDistrictId('');
                                if (errors.region) {
                                    setErrors((prev) => {
                                        const { region: _region, ...rest } = prev;
                                        return rest;
                                    });
                                }
                            }}
                        />
                        {errors.region ? (
                            <div id={`${fieldId}-region-error`} className="cr-field-error-text">
                                {errors.region}
                            </div>
                        ) : (
                            <div id={`${fieldId}-region-hint`} className="cr-field-helper">
                                Locations belong to a region. Choose a district when the region has one.
                            </div>
                        )}
                    </div>

                    <div className="cr-field">
                        <span className="cr-field-label" id={`${fieldId}-district-label`}>
                            District
                        </span>
                        <Select
                            inputId={`${fieldId}-district`}
                            ariaLabel="District"
                            placeholder="None"
                            emptyMessage={
                                regionalDivisionId
                                    ? 'No districts in this region'
                                    : 'Select a region first'
                            }
                            options={districtOptions}
                            value={districtId}
                            disabled={saving || !regionalDivisionId}
                            onChange={setDistrictId}
                        />
                    </div>
                </div>
                <div className="cr-modal-footer">
                    <Btn variant="secondary-outlined" buttonText="Cancel" buttonOnClick={onClose} />
                    <Btn
                        variant="primary"
                        buttonText={saving ? 'Saving...' : 'Create'}
                        buttonOnClick={() => void handleSubmit()}
                        buttonDisabled={saving || !hasRegions}
                    />
                </div>
            </div>
        </div>
    );
};
